/**
 * Activity Stream Panel
 *
 * A visually rich, demo-worthy activity feed component for the Live Activity Dashboard.
 * Features expandable cards, color-coded activity types, and smooth animations.
 *
 * Features:
 * - Newest events on top with slide-in animations
 * - Rich color coding per activity type
 * - Expandable cards for detailed information
 * - Subtle correction alerts (red border)
 * - Live/Paused toggle
 * - Responsive and professional design
 *
 * @author BizClick WMS
 * @version 2.0 - Redesigned for demo quality
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Activity,
  Package,
  MapPin,
  User,
  Clock,
  ArrowDown,
  ArrowUp,
  ArrowRightLeft,
  Repeat,
  Sparkles,
  Database,
  RefreshCw,
  Zap,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Bookmark,
  Truck
} from 'lucide-react';
import {
  LocationActivityEvent,
  ActivityType,
  ActivityTypeNames,
  ActivityTypeDetailedColors
} from '../../../../types/locationActivity';
import { useTranslation } from '../../../../contexts/I18nProvider';

type TFunc = (key: string, params?: Record<string, string | number | null | undefined>, defaultText?: string) => string;

interface ActivityStreamPanelProps {
  /** Events to display (will be sorted newest first) */
  events: LocationActivityEvent[];

  /** Maximum number of events to display */
  maxEvents?: number;

  /** Enable auto-scroll to newest events */
  autoScroll?: boolean;

  /** Callback when user clicks on an event */
  onEventClick?: (event: LocationActivityEvent) => void;
}

/**
 * Get icon component for activity type
 */
const getActivityIcon = (type: ActivityType) => {
  switch (type) {
    case ActivityType.Pick:
      return Package;
    case ActivityType.PutAway:
      return ArrowDown;
    case ActivityType.Transfer:
      return RefreshCw;
    case ActivityType.CorrectionOut:
      return ArrowUp;
    case ActivityType.CorrectionIn:
      return ArrowDown;
    case ActivityType.Counting:
      return CheckCircle2;
    case ActivityType.Receiving:
      return Truck;
    case ActivityType.Reservation:
      return Bookmark;
    case ActivityType.StockTransfer:
      return ArrowRightLeft;
    case ActivityType.Replenishment:
      return Repeat;
    case ActivityType.Optimization:
      return Sparkles;
    case ActivityType.InternalMove:
      return RefreshCw;
    case ActivityType.OpeningBalance:
      return Database;
    default:
      return Activity;
  }
};

/**
 * Format timestamp for display
 */
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

/**
 * Calculate time since event
 */
const getTimeSince = (timestamp: string, t: TFunc): string => {
  const now = Date.now();
  const eventTime = new Date(timestamp).getTime();
  const diffSeconds = Math.floor((now - eventTime) / 1000);

  if (diffSeconds < 5) return t('manager.modules.rackBuilderLive.activityStream.justNow', {}, 'Akkurat nå');
  if (diffSeconds < 60) return t('manager.modules.rackBuilderLive.activityStream.secondsAgo', { '0': diffSeconds }, `${diffSeconds}s siden`);

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return t('manager.modules.rackBuilderLive.activityStream.minutesAgo', { '0': diffMinutes }, `${diffMinutes}m siden`);

  const diffHours = Math.floor(diffMinutes / 60);
  return t('manager.modules.rackBuilderLive.activityStream.hoursAgo', { '0': diffHours }, `${diffHours}t siden`);
};

/**
 * Check if activity is a correction type
 */
const isCorrection = (type: ActivityType): boolean => {
  return type === ActivityType.CorrectionIn || type === ActivityType.CorrectionOut;
};

/**
 * Single activity card component with expand/collapse functionality
 */
const ActivityCard: React.FC<{
  event: LocationActivityEvent;
  index: number;
  onEventClick?: (event: LocationActivityEvent) => void;
}> = ({ event, index, onEventClick }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const colors = ActivityTypeDetailedColors[event.activityType];
  const Icon = getActivityIcon(event.activityType);
  const isCorrectionType = isCorrection(event.activityType);

  const handleClick = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const handleNavigate = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onEventClick?.(event);
  }, [event, onEventClick]);

  return (
    <div
      onClick={handleClick}
      className={`
        ${colors.bg} ${colors.border}
        ${isCorrectionType ? 'ring-2 ring-red-200' : ''}
        rounded-lg shadow-sm hover:shadow-md
        transition-all duration-300 ease-out
        cursor-pointer
        animate-slideIn
      `}
      style={{
        animationDelay: `${Math.min(index * 30, 300)}ms`,
        animationFillMode: 'backwards'
      }}
    >
      {/* Main Card Content */}
      <div className="p-3">
        {/* Header Row */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            {/* Icon */}
            <div className={`${colors.icon} p-2 rounded-lg shadow-sm flex-shrink-0`}>
              <Icon className="w-4 h-4" />
            </div>

            {/* Location & Type */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-gray-900 tracking-wide">
                  {event.binCode}
                </span>
                <span className={`${colors.badge} px-2 py-0.5 rounded-md text-xs font-semibold`}>
                  {ActivityTypeNames[event.activityType]}
                </span>
                {isCorrectionType && (
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                )}
              </div>
            </div>
          </div>

          {/* Timestamp & Expand Toggle */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex flex-col items-end text-xs text-gray-500">
              <span className="font-medium">{formatTimestamp(event.timestamp)}</span>
              <span className="text-[10px]">{getTimeSince(event.timestamp, t)}</span>
            </div>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>

        {/* Details Grid - Always Visible */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          {/* Item & Quantity */}
          <div className="flex items-center gap-1.5 text-gray-700">
            <Package className="w-3.5 h-3.5 text-gray-400" />
            <span className="font-semibold truncate">{event.itemNo}</span>
            <span className={`${colors.text} font-bold`}>×{event.quantity}</span>
          </div>

          {/* Operator */}
          <div className="flex items-center gap-1.5 text-gray-700 justify-end">
            <User className="w-3.5 h-3.5 text-gray-400" />
            <span className="font-medium">{event.operatorId}</span>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-2 border-t border-gray-200/50 animate-fadeIn">
          <div className="grid grid-cols-2 gap-3 text-xs">
            {/* Warehouse */}
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-600">{t('manager.modules.rackBuilderLive.activityStream.warehouseLabel', {}, 'Lager:')}</span>
              <span className="font-semibold text-gray-800">{event.warehouseId}</span>
            </div>

            {/* Rack Number */}
            <div className="flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-600">{t('manager.modules.rackBuilderLive.activityStream.rackLabel', {}, 'Reol:')}</span>
              <span className="font-semibold text-gray-800">{event.rackNumber}</span>
            </div>

            {/* Full Timestamp */}
            <div className="flex items-center gap-1.5 col-span-2">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-600">{t('manager.modules.rackBuilderLive.activityStream.timestampLabel', {}, 'Tidspunkt:')}</span>
              <span className="font-semibold text-gray-800">
                {new Date(event.timestamp).toLocaleString('nb-NO', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </span>
            </div>

            {/* Company (if multi-tenant context) */}
            {event.companyNo && (
              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-gray-600">{t('manager.modules.rackBuilderLive.activityStream.companyLabel', {}, 'Firma:')}</span>
                <span className="font-semibold text-gray-800">{event.companyNo}</span>
              </div>
            )}
          </div>

          {/* Navigate Button */}
          {onEventClick && (
            <button
              onClick={handleNavigate}
              className={`mt-3 w-full py-2 rounded-md text-xs font-semibold ${colors.badge} hover:opacity-80 transition-opacity`}
            >
              {t('manager.modules.rackBuilderLive.activityStream.goToLocation', { '0': event.binCode }, `Gå til lokasjon ${event.binCode}`)}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Main Activity Stream Panel Component
 */
export const ActivityStreamPanel: React.FC<ActivityStreamPanelProps> = ({
  events,
  maxEvents = 50,
  autoScroll = true,
  onEventClick
}) => {
  const { t } = useTranslation();
  const [displayEvents, setDisplayEvents] = useState<LocationActivityEvent[]>([]);
  const [isLive, setIsLive] = useState(true);
  const [timeSinceUpdate, setTimeSinceUpdate] = useState<string>('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevEventCountRef = useRef(0);

  /**
   * Update display events when new events arrive
   */
  useEffect(() => {
    // Sort by timestamp DESC (newest first)
    const sorted = [...events]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, maxEvents);

    setDisplayEvents(sorted);

    // Scroll to top if new events and live mode
    if (isLive && sorted.length > prevEventCountRef.current && scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }

    prevEventCountRef.current = sorted.length;
  }, [events, maxEvents, isLive]);

  /**
   * Update "time since" display periodically
   */
  useEffect(() => {
    const updateTimeSince = () => {
      if (displayEvents.length > 0) {
        setTimeSinceUpdate(getTimeSince(displayEvents[0].timestamp, t));
      }
    };

    updateTimeSince();
    const interval = setInterval(updateTimeSince, 5000);
    return () => clearInterval(interval);
  }, [displayEvents, t]);

  /**
   * Count corrections in current view
   */
  const correctionCount = displayEvents.filter(e => isCorrection(e.activityType)).length;

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-transparent">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          <h3 className="text-base font-bold text-gray-900">
            {t('manager.modules.rackBuilderLive.activityStream.title', {}, 'Aktivitetsstrøm')}
          </h3>
          <span className="px-2 py-0.5 text-xs font-semibold text-white bg-orange-500 rounded-full">
            {displayEvents.length}
          </span>
          {correctionCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold text-red-700 bg-red-100 rounded-full flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {correctionCount}
            </span>
          )}
        </div>

        <button
          onClick={() => setIsLive(!isLive)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
            isLive
              ? 'bg-orange-500 text-white shadow-md'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {isLive ? (
            <>
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              {t('manager.modules.rackBuilderLive.activityStream.live', {}, 'LIVE')}
            </>
          ) : (
            <>{t('manager.modules.rackBuilderLive.activityStream.paused', {}, 'PAUSET')}</>
          )}
        </button>
      </div>

      {/* Events Stream */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-3 space-y-2.5 activity-stream-scroll"
      >
        {displayEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
            <Activity className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">
              {t('manager.modules.rackBuilderLive.activityStream.waitingForActivity', {}, 'Venter på aktivitet...')}
            </p>
            <p className="text-xs mt-1 text-gray-400">
              {t('manager.modules.rackBuilderLive.activityStream.eventsAppearRealtime', {}, 'Hendelser vil vises her i sanntid')}
            </p>
          </div>
        ) : (
          displayEvents.map((event, index) => (
            <ActivityCard
              key={`${event.timestamp}-${event.binCode}-${index}`}
              event={event}
              index={index}
              onEventClick={onEventClick}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            {t('manager.modules.rackBuilderLive.activityStream.showingOfEvents', { '0': displayEvents.length, '1': events.length }, `Viser ${displayEvents.length} av ${events.length} hendelser`)}
          </span>
          {!isLive && (
            <span className="text-orange-500 font-semibold">{t('manager.modules.rackBuilderLive.activityStream.pausedLabel', {}, 'Pauset')}</span>
          )}
          {isLive && displayEvents.length > 0 && (
            <span className="text-gray-400">{t('manager.modules.rackBuilderLive.activityStream.lastLabel', { '0': timeSinceUpdate }, `Siste: ${timeSinceUpdate}`)}</span>
          )}
        </div>
      </div>

      {/* Activity Type Legend */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50/50">
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
            <span className="text-gray-500">{t('manager.modules.rackBuilderLive.streamLegend.picking', {}, 'Plukking')}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            <span className="text-gray-500">{t('manager.modules.rackBuilderLive.streamLegend.receiving', {}, 'Mottak')}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            <span className="text-gray-500">{t('manager.modules.rackBuilderLive.streamLegend.putAway', {}, 'Utlegg')}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-sky-500"></span>
            <span className="text-gray-500">{t('manager.modules.rackBuilderLive.streamLegend.stockTransfer', {}, 'Lageroverføring')}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-teal-500"></span>
            <span className="text-gray-500">{t('manager.modules.rackBuilderLive.streamLegend.replenishment', {}, 'Påfylling')}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
            <span className="text-gray-500">{t('manager.modules.rackBuilderLive.streamLegend.optimization', {}, 'Optimalisering')}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
            <span className="text-gray-500">{t('manager.modules.rackBuilderLive.streamLegend.internalMove', {}, 'Internflytt')}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            <span className="text-gray-500">{t('manager.modules.rackBuilderLive.streamLegend.correction', {}, 'Korreksjon')}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-slate-500"></span>
            <span className="text-gray-500">{t('manager.modules.rackBuilderLive.streamLegend.counting', {}, 'Telling')}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-stone-500"></span>
            <span className="text-gray-500">{t('manager.modules.rackBuilderLive.streamLegend.openingBalance', {}, 'Inngående saldo')}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default ActivityStreamPanel;
